import React, { Fragment, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { IconWarning } from '@americanexpress/dls-icons';
import { KEYCODE_ENTER, KEYCODE_ESCAPE } from '../../constants/keyConstants';
import { useDateDispatch, useDateSelector } from '../../context/dateContext';
import { useTimeSelector } from '../../context/timeContext';
import {
  getStartDate, getEndDate, getFocusedField,
  getIsStartDateWarning, getIsEndDateWarning, getIsDateRange,
} from '../../selectors/dateSelectors';
import { getEndTime, getStartTime } from '../../selectors/timeSelectors';
import DateInputMask from './DateInputMask';
import { setFocusedField } from '../../ducks/dateDuck';
import { END_DATE, START_DATE } from '../../constants/dateConstants';
import HiddenInputMaskButton from './HiddenInputMaskButton';
import { isFromButton } from '../../utils/eventUtils';
import DateInputLabel from './DateInputLabel';
import { useIsHomePageVariantHook } from '../../hooks/useIsHomePageVariantHook';

export const useEscEnterKeyboardHandler = ({
  isOpen,
  setIsOpen,
  startDateRef,
  endDateRef,
}) => {
  const focusedField = useDateSelector(getFocusedField);

  useEffect(() => {
    const closeDatePicker = (event) => {
      if (isOpen) {
        if (event.keyCode === KEYCODE_ESCAPE
          || (event.keyCode === KEYCODE_ENTER && !isFromButton(event))) {
          const focusedRef = focusedField === START_DATE ? startDateRef : endDateRef;
          // ensures correct input is in focus after closing popup via this key event
          setTimeout(() => focusedRef?.current?.focus(), 0);
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('keydown', closeDatePicker);
    return () => document.removeEventListener('keydown', closeDatePicker);
  }, [isOpen, focusedField, startDateRef, endDateRef, setIsOpen]);
};

const InputMasks = ({
  placeholder,
  setIsOpen,
  isOpen,
  startDateRef,
  endDateRef,
  onClick,
  errors,
}) => {
  const isStartDateWarning = useDateSelector(getIsStartDateWarning);
  const isEndDateWarning = useDateSelector(getIsEndDateWarning);
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const isDateRange = useDateSelector(getIsDateRange);
  const dateDispatch = useDateDispatch();

  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);

  const handleStartDateOpen = useCallback(() => {
    setIsOpen(true);
    dateDispatch(setFocusedField(START_DATE));
    onClick('start date');
  }, [setIsOpen, dateDispatch, onClick]);

  const handleEndDateOpen = useCallback(() => {
    setIsOpen(true);
    dateDispatch(setFocusedField(END_DATE));
    onClick('end date');
  }, [setIsOpen, dateDispatch, onClick]);

  const { formatMessage } = useIntl();
  const startDateInputMaskButtonId = 'date-picker-popup-button-start-date';
  const endDateInputMaskButtonId = 'date-picker-popup-button-end-date';
  const startDateInputSelectorId = 'input-selector-start-date';
  const endDateInputSelectorId = 'input-selector-context-end-date';
  const startTimeInputContextId = 'input-context-start-time';
  const endTimeInputContextId = 'input-context-end-time';
  const startDateContextId = 'input-start-date';
  const endDateContextId = 'input-end-date';
  useEscEnterKeyboardHandler({
    isOpen,
    setIsOpen,
    startDateRef,
    endDateRef,
  });
  const isVariant = useIsHomePageVariantHook();
  const endDateErrorsToShow = errors?.endDateErrors?.join(' ');
  const startDateErrorsToShow = errors?.startDateErrors?.join(' ');
  const endDateLabel = (
    <Fragment>
      <DateInputLabel
        labelText={placeholder.endDate}
        labelID={endDateContextId}
        inputID={endDateInputMaskButtonId}
      />
      <span id={endDateInputSelectorId} className="display-none" aria-hidden={true}>
        {formatMessage({ id: 'end_date' })}
      </span>
      <HiddenInputMaskButton
        isOpen={isOpen}
        onOpen={handleEndDateOpen}
        popupTriggerRef={endDateRef}
        hasWarning={isEndDateWarning}
        inputID={endDateInputMaskButtonId}
        ariaLabel={placeholder.ariaEndDate || placeholder.endDate}
        ariaDescription={`${endDateInputSelectorId} ${endTimeInputContextId} ${endDateContextId}`}
        errors={errors?.endDateErrors}
      >
        <DateInputMask
          stringDate={endDate}
          time={endTime}
          timeId={endTimeInputContextId}
          dateId={endDateContextId}
        />
      </HiddenInputMaskButton>
      {isVariant && errors?.endDateErrors && (
        <div id={`${endDateInputMaskButtonId}-status-message`} className="color-status-graphic-critical margin-1-t">
          <IconWarning color="critical" size="md" isFilled={true} className="margin-1-r" />
          { endDateErrorsToShow }
        </div>
      )}
    </Fragment>
  );

  const startDateLabel = (
    <>
      <DateInputLabel
        labelText={placeholder.startDate}
        labelID={startDateContextId}
        inputID={startDateInputMaskButtonId}
      />
      <span id={startDateInputSelectorId} className="display-none" aria-hidden={true}>
        {formatMessage({ id: 'start_date' })}
      </span>
      <HiddenInputMaskButton
        isOpen={isOpen}
        onOpen={handleStartDateOpen}
        popupTriggerRef={startDateRef}
        hasWarning={isStartDateWarning}
        inputID={startDateInputMaskButtonId}
        ariaLabel={placeholder.ariaStartDate || placeholder.startDate}
        ariaDescription={`${startDateInputSelectorId} ${startTimeInputContextId} ${startDateContextId}`}
        errors={errors?.startDateErrors}
      >
        <DateInputMask
          stringDate={startDate}
          time={startTime}
          timeId={startTimeInputContextId}
          dateId={startDateContextId}
        />
      </HiddenInputMaskButton>
      {isVariant && errors?.startDateErrors && (
        <div id={`${startDateInputMaskButtonId}-status-message`} className="color-status-graphic-critical margin-1-t">
          <IconWarning color="critical" size="md" isFilled={true} className="margin-1-r" />
          { startDateErrorsToShow }
        </div>
      )}
    </>
  );

  if (isDateRange) {
    if (isVariant) {
      return (
        <div className="row flex-align-start margin-0">
          <div className={clsx('flex flex-column flex-end col-sm-6 pad-0-lr-xs-down margin-2-b-xs-down pad-1-r-sm-up pad-0-l-sm-up')}>
            {startDateLabel}
          </div>
          <div className={clsx('flex flex-column flex-end col-sm-6 pad-0-lr-xs-down pad-0-r-sm-up pad-1-l-sm-up')}>
            {endDateLabel}
          </div>
        </div>
      );
    }
    return (
      <div className="flex row flex-align-end margin-0">
        <div className="flex flex-column col-xs-6 pad-0-l">
          {startDateLabel}
        </div>
        <div className="flex flex-column col-xs-6 pad-0-l">
          {endDateLabel}
        </div>
      </div>
    );
  }
  if (isVariant) {
    return (
      <div className="flex flex-column flex-end col-xs-12 pad-0-lr">
        {startDateLabel}
      </div>
    );
  }
  return (
    <div className="flex flex-column flex-end col-xs-12 pad-0-l">
      {startDateLabel}
    </div>
  );
};

InputMasks.propTypes = {
  placeholder: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    ariaStartDate: PropTypes.string,
    ariaEndDate: PropTypes.string,
  }).isRequired,
  setIsOpen: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  startDateRef: PropTypes.shape({
    current: PropTypes.shape({
      select: PropTypes.func,
    }),
  }),
  endDateRef: PropTypes.shape({
    current: PropTypes.shape({
      select: PropTypes.func,
    }),
  }),
  onClick: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    startDateErrors: PropTypes.arrayOf(PropTypes.string),
    endDateErrors: PropTypes.arrayOf(PropTypes.string),
  }),
};

InputMasks.defaultProps = {
  startDateRef: null,
  endDateRef: null,
};

export default InputMasks;
